import { useState, useEffect, FormEvent } from 'react';
import {
  getMembers,
  getGuardianships,
  getPlayers,
  getTeams,
  updateMember,
  Member,
  Guardianship,
  Player,
  Team,
} from '../api/client';
import TeamNav from '../components/TeamNav';

/**
 * Guardians tab, a role-lens over Members (role=guardian) that shows each
 * guardian and the children they're responsible for (via the
 * player_guardians links). Link/unlink happens on a player (Players tab →
 * edit → Guardians); identity + other roles live on the Members tab. This
 * tab adds a quick contact edit + "email all guardians".
 */

interface Coverage {
  playerName: string;
  teamName: string;
}

interface EditState {
  name: string;
  email: string;
  phone: string;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0]![0]! + (parts[1]?.[0] || '')).toUpperCase();
}

export default function Guardians() {
  const [members, setMembers] = useState<Member[]>([]);
  const [links, setLinks] = useState<Guardianship[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [editing, setEditing] = useState<Member | null>(null);
  const [editForm, setEditForm] = useState<EditState>({ name: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { void load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const [m, l, p, t] = await Promise.all([
        getMembers('guardian'),
        getGuardianships(),
        getPlayers(),
        getTeams(),
      ]);
      setMembers(m);
      setLinks(l);
      setPlayers(p);
      setTeams(t);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load guardians');
    } finally {
      setLoading(false);
    }
  }

  const playerById: Record<string, Player> = Object.fromEntries(players.map(p => [p.playerId, p]));
  const teamName: Record<string, string> = Object.fromEntries(teams.map(t => [t.id, t.name]));

  // memberId → list of players they guard
  const coverageByMember: Record<string, Coverage[]> = {};
  for (const link of links) {
    const p = playerById[link.playerId];
    if (!p) continue;
    (coverageByMember[link.memberId] ||= []).push({
      playerName: `${p.firstName} ${p.lastName}`.trim(),
      teamName: teamName[p.teamId] || '',
    });
  }

  const guardians = members
    .filter(m => m.status !== 'archived')
    .sort((a, b) => a.name.localeCompare(b.name));

  const emails = guardians.map(g => g.email).filter(Boolean);

  function openEdit(m: Member) {
    setEditing(m);
    setEditForm({ name: m.name, email: m.email, phone: m.phone });
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!editing) return;
    if (!editForm.name.trim()) { setError('Name is required'); return; }
    try {
      setSaving(true);
      await updateMember(editing.memberId, {
        name: editForm.name.trim(),
        email: editForm.email.trim(),
        phone: editForm.phone.trim(),
      });
      setSuccess(`Updated ${editForm.name.trim()}`);
      setEditing(null);
      await load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update guardian');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="tm-page-shell">
      <TeamNav />

      <div className="page-head">
        <div>
          <h1>Parents &amp; guardians</h1>
          <p className="subtitle">
            The adults responsible for your players. Link a guardian to a child on
            the Players tab (edit a player → Guardians). One parent with three kids
            shows once, covering all three.
          </p>
        </div>
        <div className="head-actions">
          {emails.length > 0 && (
            <a className="ebtn ebtn-secondary" href={`mailto:?bcc=${encodeURIComponent(emails.join(','))}`}>
              Email all ({emails.length})
            </a>
          )}
        </div>
      </div>

      {error && <div className="ealert ealert-error">{error}</div>}
      {success && <div className="ealert ealert-success">{success}</div>}

      {loading ? (
        <p className="loading">Loading guardians…</p>
      ) : guardians.length === 0 ? (
        <div className="empty-state">
          <p>No guardians yet. Add guardian details to players (Players tab → edit a player → Guardians, or the team setup wizard) and they'll appear here.</p>
        </div>
      ) : (
        <div className="card-surface">
          <table className="tm-table">
            <thead>
              <tr>
                <th>Guardian</th>
                <th>Responsible for</th>
                <th>Phone</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {guardians.map(g => {
                const covers = coverageByMember[g.memberId] || [];
                return (
                  <tr key={g.memberId}>
                    <td>
                      <div className="tm-person">
                        <span className="tm-avatar tm-avatar-empty">{initials(g.name)}</span>
                        <div>
                          <div className="tm-person-name">{g.name || '(no name)'}</div>
                          {g.email && <a className="tm-link" href={`mailto:${g.email}`}>{g.email}</a>}
                        </div>
                      </div>
                    </td>
                    <td>
                      {covers.length === 0 ? (
                        <span className="tm-muted">-</span>
                      ) : (
                        <div className="tm-chips">
                          {covers.map((c, i) => (
                            <span key={i} className="tm-chip">{c.playerName}{c.teamName ? ` · ${c.teamName}` : ''}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>{g.phone || <span className="tm-muted">-</span>}</td>
                    <td>
                      <div className="tm-actions">
                        <button className="ebtn ebtn-secondary ebtn-sm" onClick={() => openEdit(g)}>Edit</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="tm-modal-overlay" onClick={() => setEditing(null)}>
          <div className="tm-modal" onClick={e => e.stopPropagation()}>
            <div className="tm-modal-header">
              <h2>Edit guardian</h2>
              <button type="button" className="tm-modal-close" onClick={() => setEditing(null)}>×</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="tm-modal-body">
                <label className="tm-field">
                  <span>Name *</span>
                  <input type="text" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} required />
                </label>
                <div className="tm-form-row">
                  <label className="tm-field">
                    <span>Email</span>
                    <input type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} />
                  </label>
                  <label className="tm-field">
                    <span>Phone</span>
                    <input type="tel" value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} />
                  </label>
                </div>
                <small className="tm-muted">Roles &amp; archiving live on the Members tab. Link children on the Players tab.</small>
              </div>
              <div className="tm-modal-actions">
                <button type="button" className="ebtn ebtn-secondary" onClick={() => setEditing(null)}>Cancel</button>
                <button type="submit" className="ebtn ebtn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
